import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@staff-app/ui';
import { Download } from 'lucide-react';
import { useIntlExportColumns, useIntlRequestsExport } from '../hooks/useIntlRequests';

/** `YYYY-MM-DD` for an `<input type="date">`, in the browser's own timezone. */
function isoDay(d: Date): string {
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
}

/** The first of the current month — the range somebody almost always wants. */
function monthStart(): string {
  const now = new Date();
  return isoDay(new Date(now.getFullYear(), now.getMonth(), 1));
}

/**
 * Export the request summary as a CSV (requester, 2026-09-10).
 *
 * ROOT + FULFILMENT ONLY, enforced on the server — support can read every one of these rows and
 * cannot export them, because a file of customer names, addresses and phone numbers leaving the
 * building is a different act from reading a row on screen. The button is hidden from anyone
 * else, but that is UX: the CSV is built server-side precisely so the restriction is real.
 *
 * THE COLUMN LIST COMES FROM THE SERVER. There is no copy of the registry here, so a column
 * added in `intl-export.ts` appears in this dialog with no second edit — worth a round trip on a
 * dialog somebody opens once a month.
 *
 * THE DATE RANGE IS ON THE FILED DATE, read in Central time server-side so it matches the dates
 * the queue displays. It defaults to the current month with the end left open, which is the
 * range that gets asked for; both ends are optional and "everything" is a legitimate answer.
 */
export function IntlExportDialog({
  open,
  onOpenChange,
  selectedIds = [],
  onExported,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Requests ticked in the table. NON-EMPTY means export exactly these and ignore the dates
   * (requester, 2026-09-17) — an explicit selection is a more specific instruction than a range,
   * and intersecting the two would drop a row somebody deliberately picked because it fell
   * outside a window they were not looking at.
   */
  selectedIds?: number[];
  /** Called after a successful download, so the page can clear the ticks. */
  onExported?: () => void;
}) {
  const hasSelection = selectedIds.length > 0;
  const { data: columnsData, loading: columnsLoading } = useIntlExportColumns();
  const columns = columnsData?.intlExportColumns ?? [];
  const [runExport, { loading: exporting, error }] = useIntlRequestsExport();

  const [from, setFrom] = useState(monthStart);
  const [to, setTo] = useState('');
  const [picked, setPicked] = useState<string[] | null>(null);

  // The server's default selection, once it has told us what that is. `null` means "not yet
  // chosen by this operator", so the defaults arriving do not overwrite a selection in progress.
  const defaults = useMemo(() => columns.filter((c) => c.default).map((c) => c.key), [columns]);
  useEffect(() => {
    if (picked === null && defaults.length) setPicked(defaults);
  }, [defaults, picked]);

  const selection = picked ?? defaults;
  const toggle = (key: string) =>
    setPicked((cur) => {
      const base = cur ?? defaults;
      return base.includes(key) ? base.filter((k) => k !== key) : [...base, key];
    });

  const download = async () => {
    /**
     * THE EXECUTE FUNCTION REJECTS — it does not resolve with an `{ error }` on its result.
     * Without this catch a refused export threw out of the click handler as an unhandled
     * rejection: vitest flagged it as an unhandled error on an otherwise-green run, and in a
     * browser it is a red console entry on every backwards range somebody types.
     *
     * The catch is EMPTY ON PURPOSE and this is the one place that is right: `error` from the
     * hook is already the rendered surface below, and catching the same failure a second time
     * to render it here would give the dialog two error paths that can disagree. All this needs
     * to do is stop the throw from escaping.
     */
    let res;
    try {
      res = await runExport({
        variables: {
          input: {
            // IDS REPLACE THE RANGE, never narrow it — see `selectedIds`. The dates are not even
            // sent when something is ticked, so a stale value left in the pickers cannot quietly
            // filter the selection down.
            ...(hasSelection
              ? { ids: selectedIds }
              : { from: from || undefined, to: to || undefined }),
            // Sent explicitly rather than omitted-when-default: the operator ticked these boxes
            // and the file should carry what the dialog showed, even if the server's defaults
            // change between now and the next release.
            columns: selection,
          },
        },
      });
    } catch {
      return;
    }
    const csv = res.data?.intlRequestsExport;
    if (!csv) return;

    // The house pattern for a download — see `ActivityLogPage`. `revokeObjectURL` matters on a
    // dialog somebody may export from several times without reloading.
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    // Names what it CONTAINS, so three exports in a downloads folder are tellable apart — the
    // range when it is a range, the count when it is a hand-picked set.
    a.download = hasSelection
      ? `international-requests-selected-${selectedIds.length}.csv`
      : `international-requests${from ? `-${from}` : ''}${to ? `-to-${to}` : ''}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    // Clear the ticks only once the file is actually in hand. Clearing on press would lose the
    // selection on a failed export, and the operator would have to find those rows again.
    if (hasSelection) onExported?.();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="intl-export-dialog">
        <DialogHeader>
          <DialogTitle>{'Export requests'}</DialogTitle>
          <DialogDescription>
            {hasSelection
              ? `A CSV of the ${selectedIds.length} request${
                  selectedIds.length === 1 ? '' : 's'
                } you ticked. The date range does not apply to a hand-picked set — untick them all to export by date instead.`
              : 'A CSV of the requests filed in a date range. The range reads the FILED date, in the same timezone the list displays.'}
          </DialogDescription>
        </DialogHeader>

        {/* ⚠️ THE DATES ARE HIDDEN, NOT DISABLED, when rows are ticked (requester, 2026-09-17).
            Greyed-out pickers still showing "2026-09-01" invite the question "so is it filtering
            by that as well?" — and the answer is no, which is exactly the confusion a disabled
            control creates. Untick everything and they come back. */}
        {hasSelection ? null : (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="intl-export-from">{'Filed from'}</Label>
            <Input
              id="intl-export-from"
              type="date"
              className="mt-1"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              data-testid="intl-export-from"
            />
          </div>
          <div>
            <Label htmlFor="intl-export-to">{'Filed to'}</Label>
            <Input
              id="intl-export-to"
              type="date"
              className="mt-1"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              data-testid="intl-export-to"
            />
            {/* Both ends are optional and an empty one is a real answer, not an unfilled form. */}
            <p className="mt-1 text-xs text-muted-foreground">
              {'Leave either blank for open-ended.'}
            </p>
          </div>
        </div>
        )}

        <div>
          <div className="flex items-center justify-between">
            <Label>{'Columns'}</Label>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => setPicked(defaults)}
                data-testid="intl-export-defaults"
              >
                {'Default columns'}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => setPicked(columns.map((c) => c.key))}
                data-testid="intl-export-all"
              >
                {'Select all'}
              </Button>
            </div>
          </div>

          {columnsLoading ? (
            <p className="mt-2 text-sm text-muted-foreground">{'Loading columns…'}</p>
          ) : (
            /* Scrolls: there are thirty-odd columns and the dialog must not grow past the
               viewport on a laptop. The defaults sort first, so the common case needs no
               scrolling at all. */
            <div
              className="mt-2 max-h-64 overflow-y-auto rounded-md border p-3"
              data-testid="intl-export-columns"
            >
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {columns.map((c) => (
                  <label
                    key={c.key}
                    className="flex cursor-pointer items-center gap-2 text-sm"
                    htmlFor={`intl-export-col-${c.key}`}
                  >
                    <Checkbox
                      id={`intl-export-col-${c.key}`}
                      checked={selection.includes(c.key)}
                      onCheckedChange={() => toggle(c.key)}
                      data-testid={`intl-export-col-${c.key}`}
                    />
                    <span className={c.default ? 'font-medium' : 'text-muted-foreground'}>
                      {c.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        {error ? (
          // The server's words: it names an unknown column or a backwards range, which is the
          // recovery information.
          <p className="text-sm text-destructive" data-testid="intl-export-error">
            {error.message}
          </p>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {'Cancel'}
          </Button>
          <Button
            onClick={download}
            // Nothing to export with no columns, and the server would refuse — better to say so
            // by disabling than to send a request that cannot succeed.
            disabled={exporting || selection.length === 0}
            data-testid="intl-export-download"
          >
            <Download className="mr-1.5 h-4 w-4" />
            {exporting ? 'Preparing…' : `Export ${selection.length} column${selection.length === 1 ? '' : 's'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
